import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Tooltip from '../components/tooltip/Tooltip';
import { getProductLineDetail } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';

interface Installment {
  id: number;
  installment_number: number;
  due_date: string | null;
  payment_date: string | null;
  amortization: number | null;
  interest: number | null;
  amount: number;
  status_type_id: number;
  status_type_name: string;
}

interface Guarantee {
  id: number;
  id_global: string;
  created_at: string | null;
  issue_date: string | null;
  monetization_date: string | null;
  due_date: string | null;
  creditor_name?: string | null;
  amortization_type_name?: string | null;
  monetization_amount?: number | null;
  net_monetization_amount?: number | null;
  installment_default?: number;
  payment_frequency_type_name?: string | null;
  term_in_days?: number | null;
  installment_current?: number;
  installment_count?: number;
  status_type_name?: string | null;
  company_name?: string;
  creditor_id?: number | null;
  bank_operation_number?: string | null;
  amortization_type_id?: number | null;
  account_officer?: string | null;
  loan_number?: string | null;
  payment_frequency_type_id?: number | null;
  observation?: string | null;
  rate_type_id?: number | null;
  rate_type_name?: string | null;
  percentage_point?: number | null;
  grace_period?: number | null;
  weighting_percentage?: number | null;
  weighting_amount?: number | null;
  gdu_weighting_factor_id?: number | null;
  gdu_weighting_factor_name?: string | null;
  weighting_receipt?: string | null;
  installments?: Installment[];
  installment_default_count?: number;
  iva?: number | null;
  general_tax?: number | null;
  guarantee_type?: string;
  guarantee_type_name?: string;
  endorsed?: boolean | null;
  banking_entity_id?: number | null;
  estimated_payment_date?: string | null;
  drawer_id?: number | null;
  monetization_discount_rate?: number | null;
  monetization_cvba_code?: string | null;
  monetization_vat?: number | null;
  monetization_interest?: number | null;
  status_type_id?: number | null;
  epromissory_note_id?: number | null;
  settlement_date: string | null;
}

interface ProductLineDetailData {
  product_line_id: number;
  company_name: string;
  cuit: string;
  application_id: number;
  code: string;
  label: string;
  currency_id: number;
  approved_amount: number;
  total_used_amount: number;
  available_amount: number;
  installment_default_total?: number;
  installment_default_count?: number;
  guarantees: Guarantee[];
}

const ProductLineDetail = () => {
  const { cuit, id } = useParams<{ cuit: string; id: string }>();
  const navigate = useNavigate();
  const [productLineData, setProductLineData] = useState<ProductLineDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchProductLineDetail();
    }
  }, [id]);

  const fetchProductLineDetail = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProductLineDetail(id);
      if (response.success && 'data' in response) {
        setProductLineData(response.data);
      } else {
        setError('Error al cargar los datos de la línea de producto');
        navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
      }
    } catch (err) {
      setError('Error al cargar los datos de la línea de producto');
      navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMonetization = () => {
    navigate(`${routes.newMonetization}?product_line_id=${id}`);
  };

  const handleGoBack = () => {
    navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
  };

  const handleViewClicked = (row: Guarantee) => {
    // Determinar el tipo de garantía basado en el código de la línea de producto y usar la ruta específica
    if (productLineData) {
      const code = productLineData.code;
      let targetRoute: string;
      
      if (code === '003') {
        // Préstamo
        targetRoute = routes.loanDetail
          .replace(':cuit', cuit || '')
          .replace(':lineId', id || '')
          .replace(':id', row.id_global.toString());
      } else if (code === '001' || code === '002') {
        // Cheque
        targetRoute = routes.checkDetail
          .replace(':cuit', cuit || '')
          .replace(':lineId', id || '')
          .replace(':id', row.id_global.toString());
      } else if (code === '006' || code === '007') {
        // Pagaré
        targetRoute = routes.promissoryNoteDetail
          .replace(':cuit', cuit || '')
          .replace(':lineId', id || '')
          .replace(':id', row.id_global.toString());
      } else if (code === '004') {
        // Aval Comercial
        targetRoute = routes.commercialEndorsementDetail
          .replace(':cuit', cuit || '')
          .replace(':lineId', id || '')
          .replace(':id', row.id_global.toString());
      } else {
        // Fallback por si hay un código no reconocido - usar préstamo por defecto
        targetRoute = routes.loanDetail
          .replace(':cuit', cuit || '')
          .replace(':lineId', id || '')
          .replace(':id', row.id_global.toString());
      }
      
      navigate(targetRoute, { 
        state: { 
          from: location.pathname, 
          guaranteeCode: productLineData?.code 
        } 
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('es-AR')}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    
    // Parse the date as local time to avoid timezone issues
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-AR');
  };

  const getCurrencyDisplay = (currencyId: number) => {
    return currencyId === 1 ? 'Pesos argentinos' : 'Dólares';
  };

  const getTypeHeader = () => {
    if (!productLineData) return '';
    
    const guaranteeType = productLineData.code;
    
    if (guaranteeType === '003') {
      return productLineData.currency_id === 1 ? 'Prestamos pesos argentinos' : 'Prestamos dolares';
    } else {
      return productLineData.label;
    }
  };

  const getTableTitle = () => {
    if (!productLineData) return '';
    
    const guaranteeType = productLineData.code;
    
    switch (guaranteeType) {
      case '003':
        return 'Creditos asociados';
      case '001':
      case '002':
        return 'Cheques asociados';
      case '006':
      case '007':
        return 'Pagare asociados';
      case '004':
        return 'Avales comerciales asociados';
      default:
        return 'Garantías asociadas';
    }
  };

  const getTableColumns = () => {
    if (!productLineData) return [];
    
    const guaranteeType = productLineData.code;
    
    if (guaranteeType === '003') {
      // Prestamo columns - Columnas originales del prompt
      return [
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.id_global}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. creación</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.created_at)}
            </P>
          ),
          minWidth: '110px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. emisión</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.issue_date)}
            </P>
          ),
          minWidth: '110px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. vencimiento</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.due_date)}
            </P>
          ),
          minWidth: '130px',
          grow: 1.3
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>, 
          selector: (row: Guarantee) => row.creditor_name || '-',
          cell: (row: Guarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
                {row.creditor_name || '-'}
              </P>
            </Tooltip>
          ),
          minWidth: '100px',
          grow: 0.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Sist. Amortización</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.amortization_type_name || '-'}
            </P>
          ),
          minWidth: '170px',
          grow: 1.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Bruto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Neto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.net_monetization_amount ? formatCurrency(row.net_monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Mora</P>, 
          selector: (row: Guarantee) => (
            <P 
              variant='secondary-2'
              type="3" 
              weight="regular" 
              withMargin={false}
            >
              {row.installment_default ? formatCurrency(row.installment_default) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Periodicidad</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.payment_frequency_type_name || '-'}
            </P>
          ),
          minWidth: '130px',
          grow: 1.5
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Plazo</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.term_in_days || '-'}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Cuota</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {(row.installment_current !== null && row.installment_current !== undefined && 
                row.installment_count !== null && row.installment_count !== undefined) 
                ? `${row.installment_current} de ${row.installment_count}` 
                : '-'}
            </P>
          ),
          minWidth: '100px',
          grow: 1.2
        },
        { 
          name: '', 
          selector: (row: Guarantee) => row.id,
          cell: (row: Guarantee) => (
            <Icon 
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              onClick={() => handleViewClicked(row)}
            />
          ),
          minWidth: '60px',
          grow: 0.5
        }
      ];
    } else if (guaranteeType === '004') {
      // Aval Comercial columns - Similar a Cheque y Pagaré
      return [
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.id_global}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Socio partícipe / Tercero</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.company_name || productLineData.company_name}
            </P>
          ),
          minWidth: '180px',
          grow: 2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de carga</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.issue_date)}
            </P>
          ),
          minWidth: '130px',
          grow: 1.3
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de vencimiento</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.due_date)}
            </P>
          ),
          minWidth: '160px',
          grow: 1.6
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>, 
          selector: (row: Guarantee) => row.creditor_name || '-',
          cell: (row: Guarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
                {row.creditor_name || '-'}
              </P>
            </Tooltip>
          ),
          minWidth: '100px',
          grow: 0.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.status_type_name || '-'}
            </P>
          ),
          minWidth: '100px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Monto bruto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: '', 
          selector: (row: Guarantee) => row.id,
          cell: (row: Guarantee) => (
            <Icon 
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              onClick={() => handleViewClicked(row)}
            />
          ),
          minWidth: '60px',
          grow: 0.5
        }
      ];
    } else {
      // Cheque and Pagare columns - Columnas originales del prompt
      return [
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.id_global}
            </P>
          ),
          minWidth: '80px',
          grow: 1
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Socio partícipe / Tercero</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.company_name || productLineData.company_name}
            </P>
          ),
          minWidth: '180px',
          grow: 2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de carga</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.issue_date)}
            </P>
          ),
          minWidth: '130px',
          grow: 1.3
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de monetización</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.monetization_date)}
            </P>
          ),
          minWidth: '160px',
          grow: 1.6
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de liquidación</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {formatDate(row.settlement_date)}
            </P>
          ),
          minWidth: '150px',
          grow: 1.5
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>, 
          selector: (row: Guarantee) => row.creditor_name || '-',
          cell: (row: Guarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
                {row.creditor_name || '-'}
              </P>
            </Tooltip>
          ),
          minWidth: '100px',
          grow: 0.8
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.status_type_name || '-'}
            </P>
          ),
          minWidth: '100px',
          grow: 1.2
        },
        { 
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Monto bruto</P>, 
          selector: (row: Guarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px',
          grow: 1.4
        },
        { 
          name: '', 
          selector: (row: Guarantee) => row.id,
          cell: (row: Guarantee) => (
            <Icon 
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              onClick={() => handleViewClicked(row)}
            />
          ),
          minWidth: '60px',
          grow: 0.5
        }
      ];
    }
  };

  const getCurrencyCode = () => {
    return productLineData?.currency_id === 1 ? 'ARS' : 'USD';
  };

  const renderSummaryCard = (title: string, amount: number, hasMora: boolean = false, moraAmount?: number) => (
    <Card variant='neutral' modifier='regular' spacing='4'>
        <H variant="secondary-2" withMargin={false} type='4' weight='bold' modifier='strong'>
            {title}
        </H>
        <Card display='flex' alignItems='center' gap='8px'>
            <H withMargin={false} variant='secondary-2' type="2" weight="bold" modifier='strong'>
                {getCurrencyCode()} {formatCurrency(amount)}
            </H>
            {hasMora && moraAmount && moraAmount > 0 && (
                <Card display='flex' alignItems='center' gap='8px'>
                    <H variant='danger' type="3" weight="bold" withMargin={false}>
                    {getCurrencyCode()} {formatCurrency(moraAmount)}
                    </H>
                    <P variant='neutral' type="1" modifier='weak' withMargin={false}>
                    Mora
                    </P>
                </Card>
            )}
        </Card>
    </Card>
  );

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
    );
  }

  if (error || !productLineData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Línea de producto no encontrada'}</P>
        </Card>
      </Card>
    );
  }

  const isChequeOrPagare = ['001', '002', '006', '007', '004'].includes(productLineData.code);
  const isLoan = productLineData.code === '003';

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a gestión de avales"
        />
        <Card display='flex' alignItems='baseline' gap='8px'>
            <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
            Linea {productLineData.product_line_id} 
            </H>
            <H variant="secondary-2" type="3" weight="bold" modifier="strong">
            {getTypeHeader()}
            </H>
        </Card>
      </Card>

      {/* Input fields row */}
      <div style={{ display: 'grid', gridTemplateColumns: isChequeOrPagare ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div>
          <Input
            label="Razón social"
            value={productLineData.company_name}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        <div>
          <Input
            label="CUIT"
            value={productLineData.cuit}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        <div>
          <Input
            label="Tipo de operación"
            value={productLineData.label}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        <div>
          <Input
            label="Tipo de garantía"
            value={productLineData.guarantees?.[0]?.guarantee_type || '-'}
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        {isChequeOrPagare && (
          <div>
            <Input
              label="Divisa"
              value={getCurrencyDisplay(productLineData.currency_id)}
              readOnly
              size="md"
              modifier="weak"
            />
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isLoan ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', 
        gap: '16px', 
        marginBottom: '48px' 
      }}>
        {renderSummaryCard('Total', productLineData.approved_amount)}
        {renderSummaryCard('Utilizado', productLineData.total_used_amount)}
        {renderSummaryCard('Disponible', productLineData.available_amount)}
        {isLoan && (
          renderSummaryCard(
            'Cuotas afrontadas', 
            productLineData.installment_default_count || 0, 
            true,
            productLineData.installment_default_total ? productLineData.installment_default_total : undefined
          )
        )}
      </div>

      {/* Table section header */}
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {getTableTitle()}
        </H>
        <Button variant='secondary' icon='plus_icon' iconAlign='left' onClick={handleNewMonetization}>
          Nueva monetización
        </Button>
      </Card>

      {/* Table */}
      <Card margin={{ top: '3' }}>
        <Table
          columns={getTableColumns()}
          data={productLineData.guarantees}
          tableData={{ 
            page: 0, 
            size: productLineData.guarantees.length, 
            totalElements: productLineData.guarantees.length, 
            totalPages: 1 
          }}
          handleChangePage={() => {}}
        />
      </Card>
    </Card>
  );
};

export default ProductLineDetail;